import axios from 'axios';
import * as Crypto from 'expo-crypto';
import * as DocumentPicker from 'expo-document-picker';
import { FileText, Link2, Plus, Trash2, Upload } from 'lucide-react-native';
import type React from 'react';
import { useCallback, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, TextInput } from 'react-native';
import { Button } from '../../../components/primitives/Button';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Body, Caption, Label } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { useRemoveKnowledgeItem, useUploadKnowledgeItem, useUploadKnowledgePdf } from '../hooks';
import type { KnowledgeBaseItem } from '../types';

type AddMode = 'text' | 'url' | 'pdf';

interface KnowledgeManagerProps {
  agentId: string | null;
  organizationId: string;
  items: KnowledgeBaseItem[];
  onItemAdded: (item: KnowledgeBaseItem) => void;
  onItemRemoved: (itemId: string) => void;
}

async function computeChecksum(input: string): Promise<string> {
  return Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, input);
}

async function buildPdfFormData(asset: DocumentPicker.DocumentPickerAsset): Promise<FormData> {
  const formData = new FormData();
  const name = asset.name || 'document.pdf';

  if (Platform.OS === 'web') {
    // Web → the picker exposes a browser File/Blob. When absent (some pickers),
    // fetch the object URL back into a blob before appending.
    const blob = asset.file ?? (await (await fetch(asset.uri)).blob());
    formData.append('file', blob, name);
  } else {
    // Native → append the file descriptor; RN's FormData reads it from the uri.
    formData.append('file', {
      uri: asset.uri,
      name,
      type: asset.mimeType || 'application/pdf',
    } as unknown as Blob);
  }

  return formData;
}

export function KnowledgeManager({
  agentId,
  organizationId,
  items,
  onItemAdded,
  onItemRemoved,
}: KnowledgeManagerProps) {
  const { t } = useTranslation();
  const uploadItem = useUploadKnowledgeItem();
  const uploadPdf = useUploadKnowledgePdf();
  const removeItem = useRemoveKnowledgeItem();

  const [addMode, setAddMode] = useState<AddMode | null>(null);
  const [title, setTitle] = useState('');
  const [textContent, setTextContent] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const canAdd = agentId !== null;

  const resetForm = useCallback(() => {
    setAddMode(null);
    setTitle('');
    setTextContent('');
    setUrl('');
    setError(null);
  }, []);

  const submit = useCallback(
    async (dto: {
      title: string;
      sourceType: 'text' | 'url' | 'pdf';
      contentText?: string;
      sourceUri?: string;
      checksum: string;
    }) => {
      if (!agentId) return;
      try {
        const item = await uploadItem.mutateAsync({ agentId, organizationId, dto });
        onItemAdded(item);
        resetForm();
      } catch {
        setError(t('voiceAgents.wizard.knowledge.errors.uploadFailed'));
      }
    },
    [agentId, organizationId, uploadItem, onItemAdded, resetForm, t],
  );

  const handleAddText = useCallback(async () => {
    setError(null);
    const trimmedTitle = title.trim();
    const trimmedContent = textContent.trim();
    if (!trimmedTitle || !trimmedContent) {
      setError(t('voiceAgents.wizard.knowledge.errors.textRequired'));
      return;
    }
    const checksum = await computeChecksum(trimmedContent);
    await submit({
      title: trimmedTitle,
      sourceType: 'text',
      contentText: trimmedContent,
      checksum,
    });
  }, [title, textContent, submit, t]);

  const handleAddUrl = useCallback(async () => {
    setError(null);
    const trimmedTitle = title.trim();
    const trimmedUrl = url.trim();
    if (!trimmedTitle || !trimmedUrl) {
      setError(t('voiceAgents.wizard.knowledge.errors.urlRequired'));
      return;
    }
    const checksum = await computeChecksum(trimmedUrl);
    await submit({
      title: trimmedTitle,
      sourceType: 'url',
      sourceUri: trimmedUrl,
      checksum,
    });
  }, [title, url, submit, t]);

  const handlePickPdf = useCallback(async () => {
    setError(null);
    if (!agentId) return;

    const result = await DocumentPicker.getDocumentAsync({
      type: 'application/pdf',
      copyToCacheDirectory: true,
    });
    if (result.canceled || !result.assets[0]) return;

    const asset = result.assets[0];
    setIsProcessingPdf(true);
    try {
      const formData = await buildPdfFormData(asset);
      const item = await uploadPdf.mutateAsync({ agentId, organizationId, formData });
      onItemAdded(item);
      resetForm();
    } catch (e) {
      const backendMessage = axios.isAxiosError(e)
        ? (e.response?.data as { message?: string } | undefined)?.message
        : undefined;
      setError(backendMessage ?? t('voiceAgents.wizard.knowledge.errors.pdfParseFailed'));
    } finally {
      setIsProcessingPdf(false);
    }
  }, [agentId, organizationId, uploadPdf, onItemAdded, resetForm, t]);

  const handleRemove = useCallback(
    async (itemId: string) => {
      if (!agentId) return;
      setRemovingId(itemId);
      try {
        await removeItem.mutateAsync({ agentId, itemId, organizationId });
        onItemRemoved(itemId);
      } catch {
        setError(t('voiceAgents.wizard.knowledge.errors.removeFailed'));
      } finally {
        setRemovingId(null);
      }
    },
    [agentId, organizationId, removeItem, onItemRemoved, t],
  );

  if (!canAdd) {
    return (
      <Caption style={styles.hint} accessibilityLiveRegion="polite">
        {t('voiceAgents.wizard.knowledge.completeEarlierSteps')}
      </Caption>
    );
  }

  const isBusy = uploadItem.isPending || uploadPdf.isPending || isProcessingPdf;

  return (
    <VStack spacing="lg">
      <VStack spacing="sm">
        <Label style={styles.inputLabel}>{t('voiceAgents.wizard.knowledge.addLabel')}</Label>
        <HStack spacing="sm" style={styles.modeRow}>
          <ModeButton
            active={addMode === 'text'}
            icon={<FileText size={16} color={modeIconColor(addMode === 'text')} />}
            label={t('voiceAgents.wizard.knowledge.addText')}
            onPress={() => {
              resetForm();
              setAddMode('text');
            }}
            disabled={isBusy}
          />
          <ModeButton
            active={addMode === 'url'}
            icon={<Link2 size={16} color={modeIconColor(addMode === 'url')} />}
            label={t('voiceAgents.wizard.knowledge.addUrl')}
            onPress={() => {
              resetForm();
              setAddMode('url');
            }}
            disabled={isBusy}
          />
          <ModeButton
            active={addMode === 'pdf'}
            icon={<Upload size={16} color={modeIconColor(addMode === 'pdf')} />}
            label={t('voiceAgents.wizard.knowledge.addPdf')}
            onPress={() => {
              resetForm();
              setAddMode('pdf');
            }}
            disabled={isBusy}
          />
        </HStack>
      </VStack>

      {addMode === 'text' ? (
        <VStack spacing="sm">
          <TitleInput value={title} onChangeText={setTitle} t={t} />
          <TextInput
            style={[styles.input, styles.inputMultiline]}
            value={textContent}
            onChangeText={setTextContent}
            placeholder={t('voiceAgents.wizard.knowledge.textPlaceholder')}
            placeholderTextColor={colors.text.disabled}
            multiline
            accessible
            accessibilityLabel={t('voiceAgents.wizard.knowledge.textA11y')}
          />
          <Button
            variant="primary"
            size="sm"
            onPress={handleAddText}
            disabled={isBusy}
            accessibilityLabel={t('voiceAgents.wizard.knowledge.submitTextA11y')}
          >
            {uploadItem.isPending ? (
              <ActivityIndicator size="small" color={colors.foreground} />
            ) : (
              t('voiceAgents.wizard.knowledge.submitAdd')
            )}
          </Button>
        </VStack>
      ) : null}

      {addMode === 'url' ? (
        <VStack spacing="sm">
          <TitleInput value={title} onChangeText={setTitle} t={t} />
          <TextInput
            style={styles.input}
            value={url}
            onChangeText={setUrl}
            placeholder={t('voiceAgents.wizard.knowledge.urlPlaceholder')}
            placeholderTextColor={colors.text.disabled}
            autoCapitalize="none"
            keyboardType="url"
            accessible
            accessibilityLabel={t('voiceAgents.wizard.knowledge.urlA11y')}
          />
          <Button
            variant="primary"
            size="sm"
            onPress={handleAddUrl}
            disabled={isBusy}
            accessibilityLabel={t('voiceAgents.wizard.knowledge.submitUrlA11y')}
          >
            {uploadItem.isPending ? (
              <ActivityIndicator size="small" color={colors.foreground} />
            ) : (
              t('voiceAgents.wizard.knowledge.submitAdd')
            )}
          </Button>
        </VStack>
      ) : null}

      {addMode === 'pdf' ? (
        <VStack spacing="sm">
          <Caption style={styles.hint}>{t('voiceAgents.wizard.knowledge.pdfHint')}</Caption>
          <Button
            variant="outline"
            size="sm"
            onPress={handlePickPdf}
            disabled={isBusy}
            accessibilityLabel={t('voiceAgents.wizard.knowledge.pickPdfA11y')}
          >
            {isProcessingPdf ? (
              <ActivityIndicator size="small" color={colors.primary.main} />
            ) : (
              t('voiceAgents.wizard.knowledge.pickPdf')
            )}
          </Button>
        </VStack>
      ) : null}

      {error ? (
        <Body style={styles.error} accessibilityLiveRegion="assertive" accessibilityRole="alert">
          {error}
        </Body>
      ) : null}

      <VStack spacing="sm">
        <Label style={styles.inputLabel}>{t('voiceAgents.wizard.knowledge.listLabel')}</Label>
        {items.length === 0 ? (
          <Caption style={styles.hint}>{t('voiceAgents.wizard.knowledge.listEmpty')}</Caption>
        ) : (
          items.map((item) => (
            <HStack key={item.id} spacing="sm" style={styles.itemRow}>
              <ItemIcon sourceType={item.sourceType} />
              <VStack spacing="xs" style={styles.itemInfo}>
                <Body style={styles.itemTitle} numberOfLines={1}>
                  {item.title}
                </Body>
                <Caption style={styles.itemType}>
                  {t(`voiceAgents.wizard.knowledge.sourceType.${item.sourceType}`)}
                </Caption>
              </VStack>
              <Pressable
                onPress={() => handleRemove(item.id)}
                disabled={removingId === item.id}
                accessibilityRole="button"
                accessibilityLabel={`${t('voiceAgents.wizard.knowledge.deleteItemA11y')} ${item.title}`}
                style={styles.deleteBtn}
              >
                {removingId === item.id ? (
                  <ActivityIndicator size="small" color={colors.text.tertiary} />
                ) : (
                  <Trash2 size={18} color={colors.status?.error?.main ?? '#f87171'} />
                )}
              </Pressable>
            </HStack>
          ))
        )}
      </VStack>
    </VStack>
  );
}

function modeIconColor(active: boolean): string {
  return active ? colors.foreground : colors.text.secondary;
}

function ItemIcon({ sourceType }: { sourceType: KnowledgeBaseItem['sourceType'] }) {
  const color = colors.primary.main;
  if (sourceType === 'url') return <Link2 size={18} color={color} />;
  if (sourceType === 'pdf') return <FileText size={18} color={color} />;
  return <Plus size={18} color={color} />;
}

function ModeButton({
  active,
  icon,
  label,
  onPress,
  disabled,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      variant={active ? 'primary' : 'outline'}
      size="sm"
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: active }}
    >
      <HStack spacing="xs" style={styles.modeBtnInner}>
        {icon}
        <Caption style={[styles.modeBtnLabel, { color: modeIconColor(active) }]}>{label}</Caption>
      </HStack>
    </Button>
  );
}

function TitleInput({
  value,
  onChangeText,
  t,
}: {
  value: string;
  onChangeText: (v: string) => void;
  t: (key: string) => string;
}) {
  return (
    <TextInput
      style={styles.input}
      value={value}
      onChangeText={onChangeText}
      placeholder={t('voiceAgents.wizard.knowledge.titlePlaceholder')}
      placeholderTextColor={colors.text.disabled}
      accessible
      accessibilityLabel={t('voiceAgents.wizard.knowledge.titleA11y')}
    />
  );
}

const styles = StyleSheet.create({
  modeRow: {
    flexWrap: 'wrap',
    gap: 8,
  },
  modeBtnInner: {
    alignItems: 'center',
  },
  modeBtnLabel: {
    fontSize: 13,
    fontWeight: '500',
  },
  input: {
    backgroundColor: colors.background.input,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.background.elevated,
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: colors.text.primary,
    fontSize: 15,
    lineHeight: 22,
  },
  inputMultiline: {
    minHeight: 96,
    textAlignVertical: 'top',
  },
  inputLabel: {
    color: colors.text.secondary,
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 2,
  },
  hint: {
    color: colors.text.tertiary,
    lineHeight: 20,
  },
  error: {
    color: colors.status?.error?.main ?? '#f87171',
    marginTop: 4,
    lineHeight: 20,
  },
  itemRow: {
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.background.elevated,
  },
  itemInfo: {
    flex: 1,
  },
  itemTitle: {
    color: colors.text.primary,
    fontSize: 15,
  },
  itemType: {
    color: colors.text.tertiary,
    fontSize: 12,
  },
  deleteBtn: {
    padding: 6,
  },
});
